import { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo, useReducer } from 'react';

/**
 * A custom Hook that creates a constant value over the lifecycle of a component.
 *
 * @param {(function|any)} value - The value to create a constant over.
 * @return {any} The constant value.
 */
var useConst = function useConst(init) {
  var _ref$current;
  var ref = useRef();
  if (ref.current === undefined) {
    // Wrap the value in an object so that we can tell if it's initialized even if the initializer is undefined or returns undefined
    ref.current = {
      value: typeof init === 'function' ? init() : init
    };
  }
  return ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.value;
};

function _iterableToArrayLimit(arr, i) {
  var _i = null == arr ? null : "undefined" != typeof Symbol && arr[Symbol.iterator] || arr["@@iterator"];
  if (null != _i) {
    var _s,
      _e,
      _x,
      _r,
      _arr = [],
      _n = !0,
      _d = !1;
    try {
      if (_x = (_i = _i.call(arr)).next, 0 === i) {
        if (Object(_i) !== _i) return;
        _n = !1;
      } else for (; !(_n = (_s = _x.call(_i)).done) && (_arr.push(_s.value), _arr.length !== i); _n = !0);
    } catch (err) {
      _d = !0, _e = err;
    } finally {
      try {
        if (!_n && null != _i.return && (_r = _i.return(), Object(_r) !== _r)) return;
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
}
function _regeneratorRuntime() {
  _regeneratorRuntime = function () {
    return exports;
  };
  var exports = {},
    Op = Object.prototype,
    hasOwn = Op.hasOwnProperty,
    defineProperty = Object.defineProperty || function (obj, key, desc) {
      obj[key] = desc.value;
    },
    $Symbol = "function" == typeof Symbol ? Symbol : {},
    iteratorSymbol = $Symbol.iterator || "@@iterator",
    asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator",
    toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";
  function define(obj, key, value) {
    return Object.defineProperty(obj, key, {
      value: value,
      enumerable: !0,
      configurable: !0,
      writable: !0
    }), obj[key];
  }
  try {
    define({}, "");
  } catch (err) {
    define = function (obj, key, value) {
      return obj[key] = value;
    };
  }
  function wrap(innerFn, outerFn, self, tryLocsList) {
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator,
      generator = Object.create(protoGenerator.prototype),
      context = new Context(tryLocsList || []);
    return defineProperty(generator, "_invoke", {
      value: makeInvokeMethod(innerFn, self, context)
    }), generator;
  }
  function tryCatch(fn, obj, arg) {
    try {
      return {
        type: "normal",
        arg: fn.call(obj, arg)
      };
    } catch (err) {
      return {
        type: "throw",
        arg: err
      };
    }
  }
  exports.wrap = wrap;
  var ContinueSentinel = {};
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}
  var IteratorPrototype = {};
  define(IteratorPrototype, iteratorSymbol, function () {
    return this;
  });
  var getProto = Object.getPrototypeOf,
    NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  NativeIteratorPrototype && NativeIteratorPrototype !== Op && hasOwn.call(NativeIteratorPrototype, iteratorSymbol) && (IteratorPrototype = NativeIteratorPrototype);
  var Gp = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(IteratorPrototype);
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function (method) {
      define(prototype, method, function (arg) {
        return this._invoke(method, arg);
      });
    });
  }
  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if ("throw" !== record.type) {
        var result = record.arg,
          value = result.value;
        return value && "object" == typeof value && hasOwn.call(value, "__await") ? PromiseImpl.resolve(value.__await).then(function (value) {
          invoke("next", value, resolve, reject);
        }, function (err) {
          invoke("throw", err, resolve, reject);
        }) : PromiseImpl.resolve(value).then(function (unwrapped) {
          result.value = unwrapped, resolve(result);
        }, function (error) {
          return invoke("throw", error, resolve, reject);
        });
      }
      reject(record.arg);
    }
    var previousPromise;
    defineProperty(this, "_invoke", {
      value: function (method, arg) {
        function callInvokeWithMethodAndArg() {
          return new PromiseImpl(function (resolve, reject) {
            invoke(method, arg, resolve, reject);
          });
        }
        return previousPromise = previousPromise ? previousPromise.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
      }
    });
  }
  function makeInvokeMethod(innerFn, self, context) {
    var state = "suspendedStart";
    return function (method, arg) {
      if ("executing" === state) throw new Error("Generator is already running");
      if ("completed" === state) {
        if ("throw" === method) throw arg;
        return doneResult();
      }
      for (context.method = method, context.arg = arg;;) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }
        if ("next" === context.method) context.sent = context._sent = context.arg;else if ("throw" === context.method) {
          if ("suspendedStart" === state) throw state = "completed", context.arg;
          context.dispatchException(context.arg);
        } else "return" === context.method && context.abrupt("return", context.arg);
        state = "executing";
        var record = tryCatch(innerFn, self, context);
        if ("normal" === record.type) {
          if (state = context.done ? "completed" : "suspendedYield", record.arg === ContinueSentinel) continue;
          return {
            value: record.arg,
            done: context.done
          };
        }
        "throw" === record.type && (state = "completed", context.method = "throw", context.arg = record.arg);
      }
    };
  }
  function maybeInvokeDelegate(delegate, context) {
    var methodName = context.method,
      method = delegate.iterator[methodName];
    if (undefined === method) return context.delegate = null, "throw" === methodName && delegate.iterator.return && (context.method = "return", context.arg = undefined, maybeInvokeDelegate(delegate, context), "throw" === context.method) || "return" !== methodName && (context.method = "throw", context.arg = new TypeError("The iterator does not provide a '" + methodName + "' method")), ContinueSentinel;
    var record = tryCatch(method, delegate.iterator, context.arg);
    if ("throw" === record.type) return context.method = "throw", context.arg = record.arg, context.delegate = null, ContinueSentinel;
    var info = record.arg;
    return info ? info.done ? (context[delegate.resultName] = info.value, context.next = delegate.nextLoc, "return" !== context.method && (context.method = "next", context.arg = undefined), context.delegate = null, ContinueSentinel) : info : (context.method = "throw", context.arg = new TypeError("iterator result is not an object"), context.delegate = null, ContinueSentinel);
  }
  function pushTryEntry(locs) {
    var entry = {
      tryLoc: locs[0]
    };
    1 in locs && (entry.catchLoc = locs[1]), 2 in locs && (entry.finallyLoc = locs[2], entry.afterLoc = locs[3]), this.tryEntries.push(entry);
  }
  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal", delete record.arg, entry.completion = record;
  }
  function Context(tryLocsList) {
    this.tryEntries = [{
      tryLoc: "root"
    }], tryLocsList.forEach(pushTryEntry, this), this.reset(!0);
  }
  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) return iteratorMethod.call(iterable);
      if ("function" == typeof iterable.next) return iterable;
      if (!isNaN(iterable.length)) {
        var i = -1,
          next = function next() {
            for (; ++i < iterable.length;) if (hasOwn.call(iterable, i)) return next.value = iterable[i], next.done = !1, next;
            return next.value = undefined, next.done = !0, next;
          };
        return next.next = next;
      }
    }
    return {
      next: doneResult
    };
  }
  function doneResult() {
    return {
      value: undefined,
      done: !0
    };
  }
  return GeneratorFunction.prototype = GeneratorFunctionPrototype, defineProperty(Gp, "constructor", {
    value: GeneratorFunctionPrototype,
    configurable: !0
  }), defineProperty(GeneratorFunctionPrototype, "constructor", {
    value: GeneratorFunction,
    configurable: !0
  }), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, toStringTagSymbol, "GeneratorFunction"), exports.isGeneratorFunction = function (genFun) {
    var ctor = "function" == typeof genFun && genFun.constructor;
    return !!ctor && (ctor === GeneratorFunction || "GeneratorFunction" === (ctor.displayName || ctor.name));
  }, exports.mark = function (genFun) {
    return Object.setPrototypeOf ? Object.setPrototypeOf(genFun, GeneratorFunctionPrototype) : (genFun.__proto__ = GeneratorFunctionPrototype, define(genFun, toStringTagSymbol, "GeneratorFunction")), genFun.prototype = Object.create(Gp), genFun;
  }, exports.awrap = function (arg) {
    return {
      __await: arg
    };
  }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, asyncIteratorSymbol, function () {
    return this;
  }), exports.AsyncIterator = AsyncIterator, exports.async = function (innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    void 0 === PromiseImpl && (PromiseImpl = Promise);
    var iter = new AsyncIterator(wrap(innerFn, outerFn, self, tryLocsList), PromiseImpl);
    return exports.isGeneratorFunction(outerFn) ? iter : iter.next().then(function (result) {
      return result.done ? result.value : iter.next();
    });
  }, defineIteratorMethods(Gp), define(Gp, toStringTagSymbol, "Generator"), define(Gp, iteratorSymbol, function () {
    return this;
  }), define(Gp, "toString", function () {
    return "[object Generator]";
  }), exports.keys = function (val) {
    var object = Object(val),
      keys = [];
    for (var key in object) keys.push(key);
    return keys.reverse(), function next() {
      for (; keys.length;) {
        var key = keys.pop();
        if (key in object) return next.value = key, next.done = !1, next;
      }
      return next.done = !0, next;
    };
  }, exports.values = values, Context.prototype = {
    constructor: Context,
    reset: function (skipTempReset) {
      if (this.prev = 0, this.next = 0, this.sent = this._sent = undefined, this.done = !1, this.delegate = null, this.method = "next", this.arg = undefined, this.tryEntries.forEach(resetTryEntry), !skipTempReset) for (var name in this) "t" === name.charAt(0) && hasOwn.call(this, name) && !isNaN(+name.slice(1)) && (this[name] = undefined);
    },
    stop: function () {
      this.done = !0;
      var rootRecord = this.tryEntries[0].completion;
      if ("throw" === rootRecord.type) throw rootRecord.arg;
      return this.rval;
    },
    dispatchException: function (exception) {
      if (this.done) throw exception;
      var context = this;
      function handle(loc, caught) {
        return record.type = "throw", record.arg = exception, context.next = loc, caught && (context.method = "next", context.arg = undefined), !!caught;
      }
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i],
          record = entry.completion;
        if ("root" === entry.tryLoc) return handle("end");
        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc"),
            hasFinally = hasOwn.call(entry, "finallyLoc");
          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) return handle(entry.catchLoc, !0);
            if (this.prev < entry.finallyLoc) return handle(entry.finallyLoc);
          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) return handle(entry.catchLoc, !0);
          } else {
            if (!hasFinally) throw new Error("try statement without catch or finally");
            if (this.prev < entry.finallyLoc) return handle(entry.finallyLoc);
          }
        }
      }
    },
    abrupt: function (type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev && hasOwn.call(entry, "finallyLoc") && this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }
      finallyEntry && ("break" === type || "continue" === type) && finallyEntry.tryLoc <= arg && arg <= finallyEntry.finallyLoc && (finallyEntry = null);
      var record = finallyEntry ? finallyEntry.completion : {};
      return record.type = type, record.arg = arg, finallyEntry ? (this.method = "next", this.next = finallyEntry.finallyLoc, ContinueSentinel) : this.complete(record);
    },
    complete: function (record, afterLoc) {
      if ("throw" === record.type) throw record.arg;
      return "break" === record.type || "continue" === record.type ? this.next = record.arg : "return" === record.type ? (this.rval = this.arg = record.arg, this.method = "return", this.next = "end") : "normal" === record.type && afterLoc && (this.next = afterLoc), ContinueSentinel;
    },
    finish: function (finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) return this.complete(entry.completion, entry.afterLoc), resetTryEntry(entry), ContinueSentinel;
      }
    },
    catch: function (tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if ("throw" === record.type) {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }
      throw new Error("illegal catch attempt");
    },
    delegateYield: function (iterable, resultName, nextLoc) {
      return this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      }, "next" === this.method && (this.arg = undefined), ContinueSentinel;
    }
  }, exports;
}
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }
  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}
function _asyncToGenerator(fn) {
  return function () {
    var self = this,
      args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);
      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }
      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }
      _next(undefined);
    });
  };
}
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _createForOfIteratorHelper(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (!it) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;
      var F = function () {};
      return {
        s: F,
        n: function () {
          if (i >= o.length) return {
            done: true
          };
          return {
            done: false,
            value: o[i++]
          };
        },
        e: function (e) {
          throw e;
        },
        f: F
      };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }
  var normalCompletion = true,
    didErr = false,
    err;
  return {
    s: function () {
      it = it.call(o);
    },
    n: function () {
      var step = it.next();
      normalCompletion = step.done;
      return step;
    },
    e: function (e) {
      didErr = true;
      err = e;
    },
    f: function () {
      try {
        if (!normalCompletion && it.return != null) it.return();
      } finally {
        if (didErr) throw err;
      }
    }
  };
}

/**
 * A custom Hook that provides a copy method to copy text to the clipboard.
 * If anything does not work, it will output an error message and the value will be set to null.
 *
 * @return {[string, function]} The current value and a copy method.
 */
var useCopyToClipboard = function useCopyToClipboard() {
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var copy = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(text) {
      var _navigator;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if ((_navigator = navigator) !== null && _navigator !== void 0 && _navigator.clipboard) {
              _context.next = 3;
              break;
            }
            console.error('Clipboard API is not supported in this environment.');
            return _context.abrupt("return", false);
          case 3:
            _context.prev = 3;
            _context.next = 6;
            return navigator.clipboard.writeText(text);
          case 6:
            setValue(text);
            return _context.abrupt("return", true);
          case 10:
            _context.prev = 10;
            _context.t0 = _context["catch"](3);
            console.error('Clipboard write failed: ', _context.t0);
            setValue();
            return _context.abrupt("return", false);
          case 15:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[3, 10]]);
    }));
    return function copy(_x) {
      return _ref.apply(this, arguments);
    };
  }();
  return [value, copy];
};

/**
 * A modified version of the `useEffect` Hook that runs an effect only once.
 *
 * @param {function} effect - The effect to run.
 */
var useEffectOnce = function useEffectOnce(effect) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, []);
};

/**
 * A custom Hook similar to `useEffectOnce`, but only runs once when a condition is met.
 *
 * @param {function} effect - The effect to run.
 * @param {boolean} when - The condition to run the effect.
 */
var useEffectOnceWhen = function useEffectOnceWhen(effect, when) {
  var runOnceRef = useRef(false);
  var effectRef = useRef(effect);
  effectRef.current = effect;
  useEffect(function () {
    if (!when || runOnceRef.current) {
      return;
    }
    if (typeof effectRef.current === 'function') {
      effectRef.current();
    }
    runOnceRef.current = true;
  }, [when]);
};

/**
 * A custom Hook that resolves to useEffect when "window" is not in scope and useLayoutEffect in the browser.
 */
var useIsomorphicEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * A custom Hook to do escape hatch optimization for event callbacks.
 *
 * @param {function} fn - The function to be called.
 * @param {Array} dependencies - The dependencies of the function.
 * @return {function} Returns a memoized version of the event callback.
 * @see https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
 * @see https://github.com/facebook/react/issues/14099
 */
var useEventCallback = function useEventCallback(fn, dependencies) {
  var ref = useRef(function () {
    throw new Error('Cannot call an event handler while rendering.');
  });

  /**
   * The useLayoutEffect function is triggered synchronously before the DOM mutations are painted.
   * However, the useEffect function is called after the DOM mutations are painted.
   */
  useIsomorphicEffect(function () {
    ref.current = fn;
  }, [fn].concat(_toConsumableArray(dependencies !== null && dependencies !== void 0 ? dependencies : [])));
  return useCallback(function () {
    var callback = ref.current;
    return callback.apply(void 0, arguments);
  }, [ref]);
};

// TODO: move to '@tonic-ui/utils'
var noop$2 = function noop() {};

// TODO: move to '@tonic-ui/utils'
var runIfFn = function runIfFn(valueOrFn) {
  if (typeof valueOrFn === 'function') {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return valueOrFn.apply(void 0, args);
  }
  return valueOrFn;
};

/**
 * A custom Hook to manage browser event listeners.
 *
 * @param {HTMLElement} target - The target element to attach the event listener to.
 * @param {string} eventName - The name of the event to listen for.
 * @param {function} eventHandler - The event handler to call when the event is fired.
 * @param {object} [options] - Optional options to pass to the event handler.
 */
var useEventListener = function useEventListener(target, eventName, eventHandler, options) {
  var eventListener = useEventCallback(eventHandler);
  useEffect(function () {
    if (!eventHandler) {
      return noop$2;
    }
    var eventTarget = runIfFn(target);
    var isEventListenerSupported = typeof (eventTarget === null || eventTarget === void 0 ? void 0 : eventTarget.addEventListener) === 'function' && typeof (eventTarget === null || eventTarget === void 0 ? void 0 : eventTarget.removeEventListener) === 'function';
    if (!isEventListenerSupported) {
      return noop$2;
    }
    eventTarget.addEventListener(eventName, eventListener, options);
    return function () {
      eventTarget.removeEventListener(eventName, eventListener, options);
    };
  }, [target, eventName, eventHandler, eventListener, options]);
};

/**
 * A custom Hook that returns a boolean indicating if the component is hydrated.
 *
 * @return {boolean} A boolean indicating if the component is hydrated.
 *
 * • When doing Server-Side Rendering (SSR), the result will always be false.
 * • When doing Client-Side Rendering (CSR), the result will always be false on the first render, and true on subsequent renders.
 */
var useHydrated = function useHydrated() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isHydrated = _useState2[0],
    setHydrated = _useState2[1];
  useEffect(function () {
    setHydrated(true);
  }, []);
  return isHydrated;
};

/**
 * A custom Hook that returns a ref with the latest value.
 *
 * @param {any} value - The latest value.
 * @return {React.RefObject} Returns a ref object with the `current` property set to the latest value.
 */
var useLatestRef = function useLatestRef(value) {
  var ref = useRef(value);
  ref.current = value;
  return ref;
};

/**
 * A custom Hook that runs a callback at most once when a condition becomes true.
 *
 * @param {function} callback - The callback to run.
 * @param {boolean} when - The condition to run the callback.
 */
var useOnceWhen = function useOnceWhen(callback) {
  var when = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var ref = useRef(false);
  if (when && !ref.current) {
    if (typeof callback === 'function') {
      callback();
    }
    ref.current = true;
  }
};

/**
 * A custom Hook that runs a callback at most once.
 *
 * @param {function} callback - The callback to run.
 */
var useOnce = function useOnce(callback) {
  useOnceWhen(callback, true);
};

var useLatest = function useLatest(value) {
  useOnce(function () {
    console.error('Warning: The `useLatest` Hook has been renamed to `useLatestRef`.\n\nSee https://trendmicro-frontend.github.io/tonic-ui/react/latest/hooks for more information.');
  });
  return useLatestRef(value);
};

// TODO: move to '@tonic-ui/utils'
var noop$1 = function noop() {};
var getInitialState = function getInitialState(query, defaultValue) {
  if (defaultValue !== undefined) {
    return defaultValue;
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    var _window$matchMedia;
    return (_window$matchMedia = window.matchMedia(query)) === null || _window$matchMedia === void 0 ? void 0 : _window$matchMedia.matches;
  }
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[useMediaQuery] The default value should be defined for server-side rendering.');
  }
  return false;
};

/**
 * A cusom Hook that listens for matches to a CSS media query. It allows the rendering of components based on whether the media query matches or not.
 *
 * @param {string} query - The media query to match against.
 * @param {boolean} defaultValue - The default value to return if the media query is not matched.
 * @return {boolean} Returns a boolean value indicating whether the media query matches or not.
 */
var useMediaQuery = function useMediaQuery(query, defaultValue) {
  var _useState = useState(getInitialState(query, defaultValue)),
    _useState2 = _slicedToArray(_useState, 2),
    matches = _useState2[0],
    setMatches = _useState2[1];
  useEffect(function () {
    var _window, _window$matchMedia2;
    var mounted = true;
    var mql = (_window = window) === null || _window === void 0 ? void 0 : (_window$matchMedia2 = _window.matchMedia) === null || _window$matchMedia2 === void 0 ? void 0 : _window$matchMedia2.call(_window, query);
    var onChange = function onChange() {
      if (!mounted) {
        return;
      }
      setMatches(!!mql.matches);
    };
    if (!mql) {
      return noop$1;
    }
    mql.addEventListener('change', onChange);
    setMatches(!!mql.matches);
    return function () {
      mounted = false;
      mql.removeEventListener('change', onChange);
    };
  }, [query]);
  return matches;
};

/**
 * A custom Hook that merges React refs into a single memoized function.
 *
 * @param {...React.RefObject} refs
 */
var useForkRef = function useForkRef() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  return useMemo(function () {
    if (refs.every(function (ref) {
      return ref === null || ref === undefined;
    })) {
      return null;
    }
    return function (node) {
      refs.forEach(function (ref) {
        if (!ref) {
          return;
        }
        if (typeof ref === 'function') {
          ref(node);
          return;
        }
        try {
          ref.current = node;
        } catch (error) {
          throw new Error("Cannot assign value '".concat(node, "' to ref '").concat(ref, "'"));
        }
      });
    };
  }, refs); // eslint-disable-line react-hooks/exhaustive-deps
};

// TODO: move to '@tonic-ui/utils'
var noop = function noop() {};

// TODO: move to '@tonic-ui/utils'
var ownerDocument = function ownerDocument(node) {
  return (node === null || node === void 0 ? void 0 : node.ownerDocument) || document;
};
var defaultEvents = ['mousedown', 'touchstart'];

/**
 * Checks if a click happened outside a Ref. Handy for dropdowns, modals and popups etc.
 *
 * @param {React.RefObject} ref - A ref to the element to check if the click happened outside.
 * @param {function} handler - A function to call if the click happened outside the ref.
 * @param {Array<string>} [events] - An array of events to listen to. If `events` is empty, the hook will not listen to any events.
 */
var useOutsideClick = function useOutsideClick(ref, handler) {
  var events = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultEvents;
  var savedHandler = useEventCallback(function (event) {
    var _el$contains;
    var el = ref === null || ref === void 0 ? void 0 : ref.current;
    el && !(el !== null && el !== void 0 && (_el$contains = el.contains) !== null && _el$contains !== void 0 && _el$contains.call(el, event.target)) && handler(event);
  }, [handler]);
  useEffect(function () {
    if (!events) {
      return noop;
    }
    var filteredEvents = (Array.isArray(events) ? events : []).filter(function (x) {
      return typeof x === 'string';
    });
    var doc = ownerDocument(ref === null || ref === void 0 ? void 0 : ref.current);
    var _iterator = _createForOfIteratorHelper(filteredEvents),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _doc$addEventListener;
        var eventName = _step.value;
        doc === null || doc === void 0 ? void 0 : (_doc$addEventListener = doc.addEventListener) === null || _doc$addEventListener === void 0 ? void 0 : _doc$addEventListener.call(doc, eventName, savedHandler, true);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    return function () {
      var _iterator2 = _createForOfIteratorHelper(filteredEvents),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var _doc$removeEventListe;
          var eventName = _step2.value;
          doc === null || doc === void 0 ? void 0 : (_doc$removeEventListe = doc.removeEventListener) === null || _doc$removeEventListe === void 0 ? void 0 : _doc$removeEventListe.call(doc, eventName, savedHandler, true);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
    };
  }, [ref, savedHandler, events]);
};

/**
 * A custom Hook that stores the previous state or props.
 *
 * @param {any} value - The value to update the previous value with.
 * @return {any} The previous value.
 */
var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
};

var defaultToggleReducer = function defaultToggleReducer(state, nextValue) {
  return typeof nextValue === 'boolean' ? nextValue : !state;
};

/**
 * A custom Hook that toggles between boolean values. It also accepts a toggle function that can be used to change the value.
 *
 * @param {boolean} initialValue - The initial value of the toggle.
 * @param {function} [toggleReducer] - An optional reducer function that can be used to determine the next value.
 * @return {[boolean, function]} Returns an array with the current value and a function to toggle the value.
 */
var useToggle = function useToggle() {
  var initialValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  var toggleReducer = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultToggleReducer;
  return useReducer(toggleReducer, initialValue);
};

export { useConst, useCopyToClipboard, useEffectOnce, useEffectOnceWhen, useEventCallback, useEventListener, useHydrated, useIsomorphicEffect, useLatest, useLatestRef, useMediaQuery, useForkRef as useMergeRefs, useOnce, useOnceWhen, useOutsideClick, usePrevious, useToggle };
